import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { QualitativeInputTreeService } from '../../service/qualitative-input-tree.service';
import { QualitativeInputTreeActions } from './qualitative-input-tree.actions';
import { catchError, map, mergeMap, of } from 'rxjs';
import { UpdateQualitativeInputTreeMutation } from '../../../../graphql/generated';
import { RiskMapTreeService } from '../../service/riskmaptree.service';
import { MutationData } from '../../shared/graphql-mutation-response.util';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';

@Injectable()
export class QualitativeInputTreeEffects {

  loadQualitativeInputs$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(

        ofType(QualitativeInputTreeActions.loadQualitativeInputs),
        mergeMap(({ campaignId, legalEntityId, completed, params, fields, au }) =>
          rs.getQualitative(campaignId, legalEntityId, completed as boolean, params, au).pipe(
            map((riskMap) => {
              return QualitativeInputTreeActions.loadQualitativeInputsSuccess(
                {
                  qualitative: riskMap,
                  fields
                }
              );
            }),
            catchError((error) => of(QualitativeInputTreeActions.loadQualitativeInputsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllQualitativeInput$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(
        ofType(QualitativeInputTreeActions.loadAllQualitativeInputs),
        mergeMap(({ campaignId, legalEntityId, fields, au, completed, params }) =>
          rs.getAllQualitative(campaignId, legalEntityId, au).pipe(
            mergeMap((qualitative) => {
              const successAction = QualitativeInputTreeActions.loadAllQualitativeInputsSuccess({
                qualitative,
                fields
              });
              return [successAction];
            }),
            catchError((error) =>
              of(QualitativeInputTreeActions.loadAllQualitativeInputsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateQualitativeInputs$ = createEffect(
    (
      rs: QualitativeInputTreeService = inject(QualitativeInputTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(QualitativeInputTreeActions.updateQualitativeInputs),
        mergeMap(({ comments, id, opinionId, questionId, filters, au, campaignId, cptId, legalEntityId, type, completed, fields, params }) =>
          rs.updateQualitativeInput(id, questionId, opinionId, comments, filters).pipe(
            mergeMap((riskMap) => [
              QualitativeInputTreeActions.updateQualitativeInputsSuccess({
                qualitative: riskMap as MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>
              })
              // QualitativeInputTreeActions.loadQualitativeInputs({
              //   au,
              //   campaignId,
              //   legalEntityId,
              //   completed,
              //   fields,
              //   params
              // })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Qualitative Input Error',
                detail: graphQlErrorMessage(error, 'Unable to update the qualitative input'),
                life: 10000
              });
              return of(
                QualitativeInputTreeActions.updateQualitativeInputsFailure({ error }),
                QualitativeInputTreeActions.loadAllQualitativeInputs({
                  au,
                  campaignId,
                  legalEntityId,
                  completed,
                  fields,
                  params
                })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateQualitativeInputsMultiple$ = createEffect(
    (
      rs: QualitativeInputTreeService = inject(QualitativeInputTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(QualitativeInputTreeActions.updateQualitativeInputsMultiple),
        mergeMap(({ changes, filters }) =>
          rs.updateQualitativeInputMultiple(changes, filters).pipe(
            map((riskMap) => {
              return QualitativeInputTreeActions.updateQualitativeInputsMultipleSuccess(
                {
                  qualitative: riskMap as MutationData<UpdateQualitativeInputTreeMutation['updateQualitativeInputTreeQuestion']>
                }
              );
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Qualitative Input Error',
                detail: graphQlErrorMessage(error, 'Unable to update the qualitative input'),
                life: 10000
              });
              return of(QualitativeInputTreeActions.updateQualitativeInputsMultipleFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeAu$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(QualitativeInputTreeActions.updateTreeAu),
        mergeMap(({ campaignId, legalEntityId, processId, cptIds, parameterName, filter }) =>
          rs.updateTreeAu(campaignId, legalEntityId, processId, filter).pipe(
            map((riskMap) => {
              return QualitativeInputTreeActions.updateTreeAuSuccess({
                riskMap: riskMap,
                cptIds,
                parameterName
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Auditable Universe Error',
                detail: graphQlErrorMessage(error, 'Unable to update the auditable universe'),
                life: 10000
              });
              return of(QualitativeInputTreeActions.updateTreeAuFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeNote$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(QualitativeInputTreeActions.updateTreeNote),
        mergeMap(({ campaignId, legalEntityId, processCode, cptIds, parameterName, note, filter, au, type, completed, fields, params }) =>
          rs.updateTreeNote(campaignId, legalEntityId, processCode, note, filter).pipe(
            mergeMap((riskMap) => [
              QualitativeInputTreeActions.updateTreeNoteSuccess({
                riskMap,
                cptIds,
                parameterName,
                note
              }),
              // 👉 Altra azione da eseguire dopo il successo
              QualitativeInputTreeActions.loadAllQualitativeInputs({
                au,
                campaignId,
                legalEntityId,
                completed,
                fields,
                params
              })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Note Error',
                detail: graphQlErrorMessage(error, 'Unable to update the note'),
                life: 10000
              });
              return of(
                QualitativeInputTreeActions.updateTreeNoteFailure({
                  error,
                  cptIds,
                  parameterName
                })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );


  downloadQualitativeInputTree$ = createEffect(
    (rs: QualitativeInputTreeService = inject(QualitativeInputTreeService)) => {
      return this.actions$.pipe(

        ofType(QualitativeInputTreeActions.downloadQualitativeInput),
        mergeMap(({ campaignId, completed, legalEntityId, parameters, timeZone }) =>
          rs.downloadQualitativeInputTree(campaignId, completed, legalEntityId, parameters, timeZone).pipe(
            map((res) => {
              this.downloadFile(res!.data![0]!.content, res!.data![0]!.filename)
              return QualitativeInputTreeActions.downloadQualitativeInputSuccess({
                res
              });
            }),
            catchError((error) => of(QualitativeInputTreeActions.downloadQualitativeInputFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }
  constructor(private actions$: Actions) { }
}
